import { useState, useEffect, useContext, useCallback } from "react";
import { Context } from "../../index";
import { AppContext } from "../../App";
import { IBoardForProxy, ISelectedFastFilters } from "../../contexts/BoardContext";
import { FilterOptionsExtented, SelectedOptions } from "../../models/FilterModels/IFilterModel";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import FunctionSystem from "../../models/functionCode/functionsSystem";
import { getOptionsQueryParams } from "../../helpers/paramsAutoFiller";
import { IFunctionPermissions } from "../../common/api_interfaces/role/IFunctionPermissions";
import { BASE_ITEM_COLOR } from "../../constants/TagColorConst";
import { IMessageActions } from "../../common/api_interfaces/actions/IMessageActions";
import { PROJECT_SETTINGS_DEFAULT } from "../../common/constants/ProjectConst";
import { hasPermission } from "../../helpers/hasPermisson";
import { ProjectSettings } from "../../common/api_interfaces/project/ProjectSettings";
import { IAllStatusColumn } from "../../common/api_interfaces/column/IAllStatusColumn";
import { IBoardTaskDisplay, IColumnDisplay } from "../../common/api_interfaces/column/IColumnDisplay";
import { IEditColumn } from "../../common/api_interfaces/column/IEditColumn";
import { IAddColumn } from "../../common/api_interfaces/column/IAddColumn";
import { IMoveColumnData } from "../../common/api_interfaces/column/IMoveColumn";
import { IMakeColumnCommon } from "../../common/api_interfaces/column/IMakeColumnCommon";
import { IMakeColumnProxy } from "../../common/api_interfaces/column/IMakeColumnProxy";
import { ITeamMemberDisplay } from "../../common/api_interfaces/project/ITeamDisplay";
import { IIParametrsFilter } from "../../common/api_interfaces/filter/IIParametrsFilter";
import { IProjectGeneralInfo } from "../../common/api_interfaces/project/IProjectGeneralInfo";
import { IRestrictBoardView } from "../../common/api_interfaces/board/IRestrictBoardView";

export const useFuncBoard = (boardId: number) => {
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();

    // Контексты
    const { store } = useContext(Context);
    const { getData, showToast, handleShowLoader } = useContext(AppContext);

    // Состояния
    const [columns, setColumns] = useState<IColumnDisplay[]>([]);
    const [tasksToDelete, setTasksToDelete] = useState<number[]>([]);
    const [showConfirmDeleteTasks, setShowConfirmDeleteTasks] = useState<boolean>(false);
    const [selectedTasks, setSelectedTasks] = useState<number[]>([]);
    const [allStatusColumn, setAllStatusColumn] = useState<IAllStatusColumn[]>([]);
    const [selectedTasksToColumn, setSelectedTasksToColumn] = useState<number[]>([]);
    const [actionMode, setActionMode] = useState<boolean>(false);
    const [showMenu, setShowMenu] = useState<boolean>(false);
    const [columnToDelete, setColumnToDelete] = useState<IColumnDisplay>();
    const [showConfirmDeleteColumn, setShowConfirmDeleteColumn] = useState<boolean>(false);
    const [rerenderBoard, setRerenderBoard] = useState<boolean>(false);
    const [addTaskColumnId, setAddTaskColumnId] = useState<number | undefined>();
    const [showTaskPopup, setShowTaskPopup] = useState(false);
    const [accessRestrict, setAccessRestrict] = useState<IRestrictBoardView | undefined>();
    const [showConfirmDeleteBoard, setShowConfirmDeleteBoard] = useState<boolean>(false);
    const [filteredColumns, setFilteredColumns] = useState<IColumnDisplay[]>([]);
    const [projectSettings, setProjectSettings] = useState<ProjectSettings>(PROJECT_SETTINGS_DEFAULT);
    const [boardFilters, setBoardFilters] = useState<FilterOptionsExtented | undefined>(undefined);
    const [filterCounter, setFilterCounter] = useState<number>(0);
    const [boardsForProxy, setBoardsForProxy] = useState<IBoardForProxy[]>([]);
    const [relatedUsers, setRelatedUsers] = useState<ITeamMemberDisplay[]>([]);
    const [boardName, setBoardName] = useState<string>("Название доски");
    const [projectId, setProjectId] = useState<number | undefined>(store.currentProjectId);
    const [isProxyBoard, setIsProxyBoard] = useState<boolean>(false);
    const [abbreviationProject, setAbbreviationProject] = useState<string>("-");
    const [editStatusAcess, setEditStatusAcess] = useState<boolean>(false);
    const [selectedFastFilters, setSelectedFastFilters] = useState<ISelectedFastFilters>({
        priority: undefined,
        type: undefined,
        tag: undefined,
    });
    const [selectedBoardFilters, setSelectedBoardFilters] = useState<SelectedOptions>(
        store.getBoardSavedFilters(Number(boardId))
    );
    const [forceFilterChange, setForceFilterChange] = useState<boolean>(false);
    const [taskNameSearch, setTaskNameSearch] = useState<string>("");

    const [systemAccess, setSystemAccess] = useState<IFunctionPermissions[]>([]);
    const [userAccess, setUserAccess] = useState<IFunctionPermissions[]>([]);

    // Константы
    const colorOptions: string[] = BASE_ITEM_COLOR;

    useEffect(() => {
        setIsLoading(true);

        const numericBoardId = Number(boardId);

        if (!numericBoardId || numericBoardId <= 0) {
            console.warn('Invalid boardId:', boardId);
            return;
        }

        const fetchData = async () => {
            try {
                let startTime = performance.now();

                const projectCurrentId = await store.getProjectIdDashBoards(Number(boardId));
                if (projectCurrentId) {
                    await Promise.all([
                        fetchProjectData(projectCurrentId),
                        fetchBoardData(projectCurrentId)
                    ]);
                } else {
                    throw new Error("Произошла ошибка при получении id проекта")
                }

                const totalEnd = performance.now();
                // console.log(`Total execution board data upload: ${totalEnd - startTime} ms`);
            } catch (error) {
                console.error('Ошибка получения доски:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [boardId]);

    const filterTasks = useCallback((tasks: IBoardTaskDisplay[]): IBoardTaskDisplay[] => {
        if (tasks.length === 0) return tasks;

        let filteredTasks = tasks;

        if (taskNameSearch) {
            const lowerCaseSearchText = taskNameSearch.toLowerCase();
            filteredTasks = filteredTasks.filter(
                t => t.name.toLowerCase().includes(lowerCaseSearchText) ||
                    t.code.toLowerCase().includes(lowerCaseSearchText)
            );
        }

        if (selectedFastFilters.priority !== undefined) {
            const priorityId = selectedFastFilters.priority.id;
            filteredTasks = filteredTasks.filter(t => t.priority.id === priorityId);
        }

        if (selectedFastFilters.type !== undefined) {
            const typeId = selectedFastFilters.type.id;
            filteredTasks = filteredTasks.filter(t => t.type.id === typeId);
        }

        if (selectedFastFilters.tag !== undefined) {
            const tagId = selectedFastFilters.tag.id;
            filteredTasks = filteredTasks.filter(t => t.tags.some(tag => tag.id === tagId));
        }

        const { priorityIds, typeTaskIds, responsibleIds, dates } = selectedBoardFilters;

        if (priorityIds?.length) {
            const prioritySet = new Set(priorityIds);
            filteredTasks = filteredTasks.filter(t => prioritySet.has(t.priority.id));
        }

        if (typeTaskIds?.length) {
            const typeSet = new Set(typeTaskIds);
            filteredTasks = filteredTasks.filter(t => typeSet.has(t.type.id));
        }

        if (responsibleIds?.length) {
            const responsibleSet = new Set(responsibleIds);
            filteredTasks = filteredTasks.filter(t =>
                responsibleSet.has(t.responsible?.id ?? null)
            );
        }

        if (dates?.[0]) {
            const minDate = dates[0].getTime();
            filteredTasks = filteredTasks.filter(t => {
                if (!t.startDate) return false;
                return new Date(t.startDate).getTime() >= minDate;
            });
        }

        if (dates?.[1]) {
            const maxDate = dates[1].getTime();
            filteredTasks = filteredTasks.filter(t => {
                if (!t.startDate) return false;
                return new Date(t.startDate).getTime() <= maxDate;
            });
        }

        return filteredTasks;
    }, [taskNameSearch, selectedFastFilters, selectedBoardFilters]);

    const filterColumns = useCallback(() => {
        if (!columns) {
            return;
        }
        const filteredColumns = columns.map(column => ({
            ...column,
            tasks: filterTasks(column.tasks)
        }));

        setFilteredColumns(filteredColumns);
    }, [columns, filterTasks, setFilteredColumns]);



    useEffect(() => {
        const priorityIdsLength = selectedBoardFilters.priorityIds?.length ?? 0;
        const typeTaskIdsLength = selectedBoardFilters.typeTaskIds?.length ?? 0;

        const shouldResetFastFilters = !forceFilterChange && (priorityIdsLength > 0 || typeTaskIdsLength > 0);

        if (shouldResetFastFilters) {
            const hasPriorityFilter = priorityIdsLength > 0;
            const hasTypeFilter = typeTaskIdsLength > 0;

            // Сбрасываем только те быстрые фильтры, которые конфликтуют с фильтрами доски
            if (hasPriorityFilter && hasTypeFilter) {
                setSelectedFastFilters(prev => ({
                    priority: undefined,
                    type: undefined,
                    tag: prev.tag
                }));
            } else if (hasPriorityFilter) {
                setSelectedFastFilters(prev => ({
                    priority: undefined,
                    type: prev.type,
                    tag: prev.tag
                }));
            } else if (hasTypeFilter) {
                setSelectedFastFilters(prev => ({
                    priority: prev.priority,
                    type: undefined,
                    tag: prev.tag
                }));
            }
        }

        // Сохраняем фильтры в хранилище
        store.setBoardsSavedFilters(Number(boardId), selectedBoardFilters);

        // Устанавливаем флаг и запускаем фильтрацию
        setForceFilterChange(!shouldResetFastFilters);
        if (columns) {
            const filteredColumns = columns.map(column => ({
                ...column,
                tasks: filterTasks(column.tasks)
            }));
            setFilteredColumns(filteredColumns);
        }
    }, [selectedBoardFilters, taskNameSearch, forceFilterChange, boardId, store]);

    useEffect(() => {
        if (columns) {
            const filteredColumns = columns.map(column => ({
                ...column,
                tasks: filterTasks(column.tasks)
            }));
            setFilteredColumns(filteredColumns);
        }
    }, [columns, filterTasks]);

    // Функции для внутренних действий

    /** 
     * Проверка роли пользователя
    */
    const checkUserRole = async (projectId: number) => {
        const functionClass = new FunctionSystem();
        return await store.hasPermission({
            projectId: projectId,
            boardId: Number(boardId),
            functionCodes: functionClass.getProjectBoardFunction,
        });
    };

    /** 
     * Получение параметров
    */
    const parseUrlOptions = () => {
        return getOptionsQueryParams(window.location.search);
    };

    /** 
     * Проверка экшена
    */
    const handleUrlActions = (options: any) => {
        if (options.page === "board" && options.entity === "task-form" && options.action === "create") {
            setShowTaskPopup(true);
        }
    };

    /** 
     * Получение данных проекта
    */
    const fetchProjectData = async (projectCurrentId: number) => {
        try {
            if (!projectCurrentId) return;

            setProjectId(projectCurrentId);
            store.setCurrentProjectId(projectCurrentId);

            const [urlOptions] = await Promise.all([parseUrlOptions()]);

            handleUrlActions(urlOptions);

        } catch (error) {
            console.error('Error fetching project data:', error);
        }
    };

    /** 
 * Получение данных для фильтров
*/
    const getFilterData = useCallback(async () => {
        try {
            const filterParams: IIParametrsFilter = {
                projectId: store.currentProjectId,
                projectUser: true,
                taskPriority: true,
                taskType: true,
                taskStatus: false,
                role: false,
                taskTag: true,
                fileType: false,
            };

            const [filterResponse, savedFilters] = await Promise.all([
                store.filterService.getFilters(filterParams),
                store.getBoardSavedFilters(Number(boardId))
            ]);

            const { data } = filterResponse;

            const options: FilterOptionsExtented = {
                responsible: data.projectUsers,
                priority: data.taskPriority,
                typeTask: data.taskType,
                tag: data.taskTag,
                date: true
            };

            setBoardFilters(options);
            setSelectedBoardFilters(savedFilters);
        } catch (error: any) {
            if (axios.isAxiosError(error)) {
                const errorMessage = error.response?.data.message || 'Ошибка загрузки фильтров';
                showToast(errorMessage);
            } else {
                console.error('Unexpected error in getFilterData:', error);
                showToast('Произошла непредвиденная ошибка');
            }
            return undefined;
        }
    }, [store.currentProjectId, boardId, store.filterService, store.getBoardSavedFilters, showToast]);

    /** 
     * Получение данных доски
    */

    const fetchBoardData = useCallback(async (projectCurrentId: number) => {
        const numericBoardId = Number(boardId);

        if (!projectCurrentId && projectCurrentId === 0) {
            return;
        }

        try {
            const [projectSettingsResponse, boardDataResponse, statusColumnResponse] = await Promise.all([
                store.projectService.getProjectSettings(projectCurrentId),
                store.getAllColumnsBoard(numericBoardId),
                store.dashBoardService.getAllStatusColumn(numericBoardId)
            ]);

            // Если нет доски - сразу выходим
            if (!boardDataResponse) {
                navigate(`/projects`);
                return;
            }

            setProjectSettings(projectSettingsResponse.data);
            setEditStatusAcess(projectSettingsResponse.data.changeColumnStatus === true);
            setAllStatusColumn(statusColumnResponse.data);

            // Параллельная загрузка дополнительных данных
            const [systemRole, userRole, projectInfo, accessData]:
                [IFunctionPermissions[], IFunctionPermissions[], number | IProjectGeneralInfo | null, IRestrictBoardView | undefined, undefined]
                = await Promise.all([
                    store.hasPermission({
                        projectId: null,
                        boardId: null,
                        functionCodes: ["BoardAction"],
                    }),
                    checkUserRole(projectCurrentId),
                    store.getProjectGeneralInfo(projectCurrentId),
                    store.restrictAccess(projectCurrentId),
                    getFilterData()
                ]);

            setSystemAccess(systemRole);
            setUserAccess(userRole);
            setColumns(store.columns);
            setBoardName(store.currentBoardData.label);
            setAccessRestrict(accessData);

            if (projectInfo && typeof projectInfo !== "number") {
                setAbbreviationProject(projectInfo.abbreviation);
            }

            if (!accessData) return;

            // Проверка доступа пользователя
            const userEmail = store.user.email;
            const userRoleIds = accessData.person
                .filter(person => person.email === userEmail)
                .map(person => person.personRoleId);

            const hasBoardAccess = accessData.boards.filter(board =>
                !board.personRoleIds?.some(roleId => userRoleIds.includes(roleId))
            );

            const userHasAccess = hasBoardAccess.map(board => board.id).includes(numericBoardId) ||
                hasPermission(systemRole, "BoardAction", "view");

            if (!userHasAccess) {
                showToast("У вас нет доступа к этой доске!");
                navigate(`/project/${projectCurrentId}`);
                return;
            }

            // Загрузка данных для авторизованного пользователя
            await loadAuthorizedUserData(projectCurrentId, numericBoardId, systemRole);

        } catch (error) {
            handleFetchError(error);
            return undefined;
        }
    }, [
        // Зависимости для useCallback
        projectId,
        boardId,
        navigate,
        showToast,
        // store зависимости (если store стабилен, можно не добавлять)
        store.projectService,
        store.getAllColumnsBoard,
        store.dashBoardService,
        store.user,
        store.columns,
        store.currentBoardData,
        // setState функции
        setProjectSettings,
        setEditStatusAcess,
        setAllStatusColumn,
        setSystemAccess,
        setUserAccess,
        setColumns,
        setBoardName,
        setProjectId,
        setAbbreviationProject,
        // Вспомогательные функции
        checkUserRole,
        getFilterData,
        hasPermission
    ]);

    // Вспомогательные функции можно вынести наружу или обернуть в useCallback
    const loadAuthorizedUserData = useCallback(async (projectId: number, boardId: number, systemRole: any) => {
        await store.getAllColumnsBoard(boardId);
        setColumns(store.columns);
        setBoardName(store.currentBoardData.label);
        setProjectId(projectId);

        const [teamResponse, boardResponse] = await Promise.all([
            store.projectService.getProjectTeamDisplayData({
                filters: {
                    projectId,
                    name: undefined,
                    role: undefined,
                },
            }),
            store.dashBoardService.getProjectBoards(projectId)
        ]);

        if (teamResponse.data.team?.length > 0) {
            setRelatedUsers(teamResponse.data.team);
        }

        const boards = boardResponse.data;
        setBoardsForProxy(boards.filter(b => b.id !== boardId));

        const currentDashboard = boards.find(b => b.id === boardId);
        if (currentDashboard) {
            setIsProxyBoard(currentDashboard.isProxyBoard);
        }
    }, [
        store.getAllColumnsBoard,
        store.projectService,
        store.dashBoardService,
        store.columns,
        store.currentBoardData,
        setColumns,
        setBoardName,
        setProjectId,
        setRelatedUsers,
        setBoardsForProxy,
        setIsProxyBoard
    ]);

    const handleFetchError = useCallback((error: unknown) => {
        if (axios.isAxiosError(error)) {
            const errorMessage = error.response?.data.message || 'Ошибка загрузки доски';
            showToast(errorMessage);
        } else {
            console.error('Ошибка получения данных по доске:', error);
            showToast('Произошла непредвиденная ошибка');
        }
    }, [showToast]);

    // Функции для внешнего доступа

    const handleDeleteColumn = useCallback((columnId: number) => {
        const column = columns.find((c) => c.id === columnId);
        if (!column) return;

        setShowMenu(false);
        setColumnToDelete(column);
        setShowConfirmDeleteColumn(true);
    }, [columns]);

    const handleMoveColumn = useCallback(async (columnId: number, position: number) => {
        try {
            const moveColumnData: IMoveColumnData = {
                boardId: Number(boardId),
                columnId,
                position,
            };

            await store.columnService.moveColumn(moveColumnData);
            await store.getAllColumnsBoard(Number(boardId));
            setColumns(store.columns);
        } catch (err: any) {
            if (axios.isAxiosError(err)) {
                showToast(err.response?.data?.message);
            }
        }
    }, [store, boardId, showToast]);

    const handleAddColumn = useCallback(async (
        name: string,
        color: string,
        weight: number,
        columnStatus: number | undefined,
        columnPriority: number | undefined,
        proxyBoardName?: string,
        proxyBoardId?: number
    ) => {
        try {
            handleShowLoader(true);
            const addColumnData: IAddColumn = {
                boardId: Number(boardId),
                name,
                color,
                weight,
                statusId: columnStatus,
                priorityId: columnPriority,
                proxyBoardName,
            };

            await store.columnService.addColumn(addColumnData);
            await store.getAllColumnsBoard(Number(boardId));

            setColumns(store.columns);
            setRerenderBoard(prev => !prev);
            showToast("Колонка создана");
        } catch (error: any) {
            if (axios.isAxiosError(error)) {
                showToast(error.response?.data?.message);
            }
        } finally {
            handleShowLoader(false);
        }
    }, [store, boardId, showToast]);

    const handleMakeColumnCommon = useCallback(async (columnId: number) => {
        try {
            handleShowLoader(true);
            const makeColumnCommonData: IMakeColumnCommon = {
                columnId,
                boardId: Number(boardId),
            };

            await store.columnService.makeColumnCommon(makeColumnCommonData);
            await getData();
            await store.getAllColumnsBoard(Number(boardId));
            setColumns(store.columns);
        } catch (err: any) {
            if (axios.isAxiosError(err)) {
                showToast(err.response?.data?.message);
            }
        } finally {
            handleShowLoader(false);
        }
    }, [store, boardId, showToast, getData]);

    const handleMakeColumnProxy = useCallback(async (
        columnId: number,
        proxyBoardName?: string,
        proxyBoardId?: number
    ) => {
        try {
            handleShowLoader(true);
            if (!proxyBoardName)
                return
            const makeColumnProxyData: IMakeColumnProxy = {
                columnId,
                boardId: Number(boardId),
                proxyBoardName,
            };

            await store.columnService.makeColumnProxy(makeColumnProxyData);
            await getData();
            await store.getAllColumnsBoard(Number(boardId));
            setColumns(store.columns);
        } catch (err: any) {
            if (axios.isAxiosError(err)) {
                showToast(err.response?.data?.message);
            }
        } finally {
            handleShowLoader(false);
        }
    }, [store, boardId, showToast, getData]);

    const handleDeleteTask = useCallback(async (taskId: number) => {
        try {
            await store.taskService.deleteTasks({ tasks: [taskId] });
            await store.getAllColumnsBoard(Number(boardId));
            setColumns(store.columns);
            showToast("Задача удалена");
        } catch (error) {
            if (axios.isAxiosError(error)) {
                showToast(error.response?.data?.message);
            }
        }
    }, [store, boardId, showToast]);

    const handleDeleteTasks = useCallback((taskIds: number[]) => {
        setTasksToDelete(taskIds);
        setShowConfirmDeleteTasks(true);
    }, []);

    const handleSelectTask = useCallback((taskId: number) => {
        setSelectedTasks(prevSelectedTasks =>
            prevSelectedTasks.includes(taskId)
                ? prevSelectedTasks.filter(id => id !== taskId)
                : [...prevSelectedTasks, taskId]
        );
    }, []);

    const handleSelectTasks = useCallback((taskIds: number[]) => {
        setSelectedTasks(taskIds);
    }, []);

    const handleMoveTask = useCallback(async (taskId: number, newColumnId: number, position: number) => {
        const oldColumn = columns.find((c) => c.tasks.some((t) => t.id === taskId));
        const newColumn = columns.find((c) => c.id === newColumnId);

        if (!oldColumn || !newColumn || position < 0) return;

        const task = oldColumn.tasks.find((t) => t.id === taskId);
        if (!task) return;

        try {
            const oldColumns = [...columns];
            const newStatus = !task.parentTaskId
                ? allStatusColumn.find(xx => xx.id === newColumn.status)
                : task.status;

            if (!newStatus) return;

            const updatedTask = {
                ...task,
                status: {
                    name: newStatus.name,
                    style: newStatus.style
                }
            } as IBoardTaskDisplay;

            // Оптимистичное обновление UI
            const updatedColumns = columns.map((column) => {
                if (column.id === oldColumn.id && column.id === newColumn.id) {
                    // Перемещение внутри одной колонки
                    const tasks = column.tasks.filter(t => t.id !== taskId);
                    tasks.splice(position, 0, updatedTask);
                    return { ...column, tasks };
                } else if (column.id === oldColumn.id) {
                    // Удаляем задачу из старой колонки
                    return { ...column, tasks: column.tasks.filter((t) => t.id !== taskId) };
                } else if (column.id === newColumn.id) {
                    // Добавляем задачу в новую колонку
                    const tasks = [...column.tasks];
                    tasks.splice(position, 0, updatedTask);
                    return { ...column, tasks };
                }
                return column;
            });

            setColumns(updatedColumns);

            const reqBody: IMessageActions = {
                boardId: boardId,
                tasks: [{
                    taskId: task.id
                }],
                actionParams: {
                    changeColumnParam: { columnId: newColumn.id, position: position }
                }
            }

            await store.taskService.executeActions(reqBody);

        } catch (error) {
            setColumns(columns);
            if (axios.isAxiosError(error)) {
                showToast(error.response?.data?.message || "Произошла ошибка при перемещении задачи");
            } else {
                showToast("Произошла ошибка при перемещении задачи");
            }
        }
    }, [columns, allStatusColumn, store, boardId, showToast]);

    const handleMoveTasks = useCallback(async (columnId: number) => {
        try {
            const reqBody: IMessageActions = {
                boardId: boardId,
                tasks: selectedTasks.map(id => ({ taskId: id })),
                actionParams: {
                    changeColumnParam: { columnId: columnId }
                }
            }

            await store.taskService.executeActions(reqBody);
            await store.getAllColumnsBoard(Number(boardId));

            setColumns(store.columns);
            setSelectedTasks([]);
            setSelectedTasksToColumn([]);
            setActionMode(false);

        } catch (error) {
            if (axios.isAxiosError(error)) {
                showToast(error.response?.data?.message);
            }
        }
    }, [selectedTasksToColumn, selectedTasks, store, boardId, showToast]);

    const cancelActionMode = useCallback(() => {
        setActionMode(prev => !prev);
        setSelectedTasks([]);
    }, []);

    const handleMoveTasksOutsideBoard = useCallback(async (boardOutsideId: number) => {
        try {
            const reqBody: IMessageActions = {
                boardId: boardId,
                tasks: selectedTasks.map(id => ({ taskId: id })),
                actionParams: {
                    changeColumnParam: { boardId: boardOutsideId }
                }
            }

            await store.taskService.executeActions(reqBody);
            await store.getAllColumnsBoard(Number(boardId));

            setColumns(store.columns);
            setSelectedTasks([]);
            setSelectedTasksToColumn([]);
            setActionMode(false);
        } catch (error) {
            if (axios.isAxiosError(error)) {
                showToast(error.response?.data?.message);
            }
        }
    }, [selectedTasks, selectedTasksToColumn, store, boardId, showToast]);

    const handleMoveTaskBoard = useCallback(async (taskId: number, toBoardId: number) => {
        try {
            const oldColumn = columns.find((c) => c.tasks.some((task) => task.id === taskId));
            if (!oldColumn) return;

            const task = oldColumn.tasks.find((t) => t.id === taskId);
            if (!task) return;

            const reqBody: IMessageActions = {
                boardId: boardId,
                tasks: [{ taskId }],
                actionParams: {
                    changeColumnParam: { boardId: toBoardId }
                }
            }

            await store.taskService.executeActions(reqBody);

            await store.getAllColumnsBoard(Number(boardId));
            setColumns(store.columns);
        } catch (error) {
            if (axios.isAxiosError(error)) {
                showToast(error.response?.data?.message);
            }
        }
    }, [columns, store, boardId, showToast]);

    const handleSelectTaskToColumn = useCallback((taskToColumnId: number) => {
        setSelectedTasksToColumn(prev =>
            prev.includes(taskToColumnId)
                ? prev.filter(id => id !== taskToColumnId)
                : [...prev, taskToColumnId]
        );
    }, []);

    const handleSelectTasksToColumn = useCallback((taskToColumnIds: number[]) => {
        setSelectedTasksToColumn(taskToColumnIds);
    }, []);

    const openMenu = useCallback(() => {
        setShowMenu(true);
    }, []);

    const closeMenu = useCallback(() => {
        setShowMenu(false);
    }, []);

    const openAddTaskForm = useCallback((columnId: number) => {
        setAddTaskColumnId(columnId);
        setShowTaskPopup(true);
    }, []);

    const handleEditColumn = useCallback(async (
        columnId: number,
        newName?: string,
        newColor?: string,
        newWeight?: number,
        newStatus?: number
    ) => {
        handleShowLoader(true);

        if (newName && newName.trim().length <= 5) {
            showToast("Наименование колонки должно быть больше 5 символов");
            handleShowLoader(false);
            return;
        }

        const editColumnData: IEditColumn = {
            columnId,
            boardId: Number(boardId),
            name: newName?.trim() ?? undefined,
            color: newColor,
            weight: newWeight,
            status: newStatus
        };

        try {
            await store.columnService.editColumn(editColumnData);
            await store.getAllColumnsBoard(Number(boardId));

            setColumns(store.columns);
            showToast("Изменения сохранены");
        } catch (err: any) {
            console.error("Ошибка при редактировании колонки:", err);
            if (axios.isAxiosError(err)) {
                showToast(err.response?.data?.message || "Ошибка при сохранении изменений");
            } else {
                showToast("Ошибка при сохранении изменений");
            }
        } finally {
            handleShowLoader(false);
        }
    }, [store, boardId, showToast]);

    const handleRenameBoard = useCallback(async (newName: string) => {
        try {
            handleShowLoader(true);
            await store.editBoard({ id: Number(boardId), name: newName });

            setBoardName(newName);
            showToast("Изменения сохранены");
            getData();
        } catch (error) {
            console.error("Ошибка при переименовании доски:", error);
            showToast("Ошибка при переименовании доски");
        } finally {
            handleShowLoader(false);
        }
    }, [store, boardId, showToast, getData]);

    const handleDeleteBoard = useCallback(() => {
        setShowMenu(false);
        setShowConfirmDeleteBoard(true);
    }, []);

    const handleTaskAction = useCallback(async () => {
        try {
            await store.getAllColumnsBoard(Number(boardId));
            setColumns(store.columns);
            setShowTaskPopup(false);
        } catch (error) {
            console.error("Ошибка в handleTaskAction:", error);
            showToast("Ошибка при обновлении данных");
        }
    }, [store, boardId]);

    const changeFastFilters = useCallback((fastFilters: ISelectedFastFilters) => {
        setSelectedFastFilters(fastFilters);

        setSelectedBoardFilters(prev => ({
            ...prev,
            priorityIds: fastFilters.priority ? [fastFilters.priority.id] : undefined,
            typeTaskIds: fastFilters.type ? [fastFilters.type.id] : undefined
        }));

        setForceFilterChange(true);
    }, []);

    const deleteTasks = async () => {
        try {
            await Promise.all([
                store.taskService.deleteTasks({ tasks: tasksToDelete }),
                store.getAllColumnsBoard(Number(boardId))
            ]);

            setColumns(store.columns);
            setShowConfirmDeleteTasks(false);
            setSelectedTasks([]);
            showToast("Задачи удалены");

        } catch (error: unknown) {
            if (axios.isAxiosError(error)) {
                const errorMessage = error.response?.data?.message || 'Ошибка при удалении задач';
                showToast(errorMessage);
            } else {
                console.error('Unexpected error in deleteTasks:', error);
                showToast('Произошла непредвиденная ошибка');
            }
        }
    };

    const deleteColumn = async () => {
        if (!columnToDelete) return;

        try {
            await store.columnService.deleteColumn({
                columnId: columnToDelete.id,
                boardId: Number(boardId),
            });

            await store.getAllColumnsBoard(Number(boardId));

            setShowConfirmDeleteColumn(false);
            setColumns(store.columns);
            showToast("Колонка удалена");

        } catch (error: unknown) {
            if (axios.isAxiosError(error)) {
                const errorMessage = error.response?.data?.message || 'Ошибка при удалении колонки';
                showToast(errorMessage);
            } else {
                console.error('Unexpected error in deleteColumn:', error);
                showToast('Произошла непредвиденная ошибка');
            }
        }
    };

    const deleteBoard = async () => {
        try {
            navigate(`/project/${projectId}`);
            await store.deleteBoard({ boardId: Number(boardId) });
            await getData();

            setShowConfirmDeleteBoard(false);
            showToast("Канбан доска удалена");

        } catch (error: unknown) {
            if (axios.isAxiosError(error)) {
                const errorMessage = error.response?.data?.message || 'Ошибка при удалении доски';
                showToast(errorMessage);
            } else {
                console.error('Unexpected error in deleteBoard:', error);
                showToast('Произошла непредвиденная ошибка');
            }
        }
    };

    const resetFilter = useCallback(() => {
        setTaskNameSearch("");
        setSelectedBoardFilters({});
        setSelectedFastFilters({
            priority: undefined,
            type: undefined,
            tag: undefined,
        });
    }, []);

    // Возвращаем сетеры и функции
    return {
        isLoading,

        columns,
        setColumns,
        tasksToDelete,
        setTasksToDelete,
        showConfirmDeleteTasks,
        setShowConfirmDeleteTasks,
        selectedTasks,
        setSelectedTasks,
        allStatusColumn, setAllStatusColumn,
        selectedTasksToColumn,
        setSelectedTasksToColumn,
        actionMode,
        setActionMode,
        showMenu,
        setShowMenu,
        columnToDelete,
        setColumnToDelete,
        showConfirmDeleteColumn,
        setShowConfirmDeleteColumn,
        rerenderBoard,
        setRerenderBoard,
        addTaskColumnId,
        setAddTaskColumnId,
        showTaskPopup,
        setShowTaskPopup,
        showConfirmDeleteBoard,
        setShowConfirmDeleteBoard,
        filteredColumns,
        setFilteredColumns,
        projectSettings,
        setProjectSettings,
        boardFilters,
        setBoardFilters,
        filterCounter,
        setFilterCounter,
        boardsForProxy,
        setBoardsForProxy,
        relatedUsers,
        setRelatedUsers,
        boardName,
        setBoardName,
        projectId,
        setProjectId,
        isProxyBoard,
        setIsProxyBoard,
        abbreviationProject,
        setAbbreviationProject,
        accessRestrict,
        editStatusAcess,
        setEditStatusAcess,
        selectedFastFilters,
        setSelectedFastFilters,
        selectedBoardFilters,
        setSelectedBoardFilters,
        forceFilterChange,
        setForceFilterChange,
        taskNameSearch,
        setTaskNameSearch,
        userAccess,
        systemAccess,

        colorOptions,

        handleDeleteColumn,
        handleMoveColumn,
        handleAddColumn,
        handleMakeColumnCommon,
        handleMakeColumnProxy,
        handleDeleteTask,
        handleDeleteTasks,
        handleSelectTask,
        handleSelectTasks,
        handleMoveTask,
        handleMoveTasks,
        cancelActionMode,
        handleMoveTasksOutsideBoard,
        handleMoveTaskBoard,
        handleSelectTaskToColumn,
        handleSelectTasksToColumn,
        openMenu,
        closeMenu,
        openAddTaskForm,
        handleEditColumn,
        handleRenameBoard,
        handleDeleteBoard,
        handleTaskAction,
        changeFastFilters,
        resetFilter,
        deleteTasks,
        deleteColumn,
        deleteBoard
    };
};